import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { Duration } from 'ts-luxon';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { DelayApiService, DelayChannel } from '../../api';
import {
	DelayChannelMutationGQL,
	DelayChannelQueryGQL,
	DelayChannelSubscriptionGQL
} from '../graphql/generated/delay.generated';
import { mapDelayChannelFromSysApi } from '../mappers';

type DelayChanelUpdate = Pick<DelayChannel['features']['delay'], 'enabled'> & { delay: string };

@Injectable()
export class DaiDeviceUiDspDelayService implements DelayApiService {
	private readonly logger: ILogger;

	private readonly delayChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly delayChannelQueryGQL: DelayChannelQueryGQL,
		private readonly delayChannelMutationGQL: DelayChannelMutationGQL,
		delayChannelSubscriptionGQL: DelayChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspDelayService');

		this.delayChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspDelayService',
				changeTypes: [NodeChangeType.AudioChannelDelay, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: delayChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<DelayChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying delay channel', { channelId, queryId });

		return this.delayChannelQueryGQL.watch({ channelId }, { fetchPolicy: 'network-only' }).valueChanges.pipe(
			map((query) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getChannel$()', 'Query updated', { channelId });
					return mapDelayChannelFromSysApi(query.data.node);
				}

				this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((channel) => this.delayChannelSubscriptionManager.subscribe(queryId, channel.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
				this.delayChannelSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public toggleDelay(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleDelay()', 'Toggling delay', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}

	public setDelayDuration(channelId: string, delay: number): Observable<Response<void, string>> {
		this.logger.trace('setDelayDuration()', 'Setting delay duration', { channelId, delay });
		return this.updateChannel(channelId, { delay: <string>Duration.fromMillis(delay).toISO() });
	}

	private updateChannel(channelId: string, update: Partial<DelayChanelUpdate>): Observable<Response<void, string>> {
		return this.delayChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								delay: { ...update }
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}
}
